import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FraudRuleBaseDTO,
    FraudRulesProductLinesDTO,
    FraudRuleTypeEnum,
    IMultiLanguageDTO,
    RepeatedTransactionsConfig,
    SingleTransactionConfig,
} from '@modeso/types__dgoods-cart';
import { CreateFraudRuleBaseRequestDTO } from '@modeso/types__dgoods-cart/dist/interfaces/fraudRules/base/CreateFraudRuleBaseRequestDTO.interface';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { UserRoles } from '../../../shared/enum/userrole.enum';
import { FraudPopupsFormValue } from '../../../shared/models/interfaces/FraudPopupsFormValue.interface';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { UploadService } from '../../../shared/services/upload.service';
import { IUploadURL } from '../../../shared/services/uploadUrl.model';
import { FileUploadUtil } from '../../../shared/util/fileUtil';
import { PermissionHelper } from '../../../shared/util/permission.helper';
import { resetCartStateFlags } from '../../../state/app.state.actions';
import {
    createFraudRule,
    getAvailableProductLines,
    loadFraudRules,
    updateFraudRule,
} from '../../../state/cart/actions/fraud-rules.actions';
import {
    selectCartError,
    selectCartLoadingState,
    selectCartSuccessState,
} from '../../../state/cart/selectors/cart.selectors';
import {
    selectFraudRuleById,
    selectFraudRulesAvailableProductLines,
} from '../../../state/cart/selectors/fraud-rules.selectors';
import { ConfirmationDialogComponent } from '../../Dialogs/confirmation-dialog.component';
import { DescriptionEditorDialog } from '../../Dialogs/descriptionEditorDialog/descriptionEditorDialog';
import { FraudRulesConstants } from '../constants';

@Component({
    selector: 'app-create-fraud-rule',
    templateUrl: './create-fraud-rule.page.html',
    styleUrls: ['./create-fraud-rule.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateFraudRulePage implements OnInit, OnDestroy {
    public readonly ruleTypes = FraudRuleTypeEnum;
    public ruleForm: FormGroup;
    public hasAccess = false;
    public isEditMode = false;
    public ruleId: string | null = null;
    public availableProductsSingle: Set<string> = new Set<string>();
    public availableProductsRepeated: Set<string> = new Set<string>();
    public availableProducts$: Observable<FraudRulesProductLinesDTO>;
    public loading$: Observable<boolean>;
    public success$: Observable<boolean>;
    public httpError$: Observable<HttpErrorResponse> | null;
    public errorMessage: string | null = null;
    public imageErrorMessage: string | null = null;
    public successMessage: string | null = null;
    public isNoProductsAvailable = false;
    public isImageUploading = false;
    public readonly LANGUAGES = FraudRulesConstants.LANGUAGES;
    public readonly IMAGE_TOOLTIP_TEXT = FraudRulesConstants.IMAGE_TOOLTIP;
    public readonly THRESHOLD_VALUE_TOOLTIP_TEXT = FraudRulesConstants.THRESHOLD_VALUE_TOOLTIP;
    public readonly REPEATED_TRANSACTIONS_TOOLTIP_TEXT = FraudRulesConstants.NUMBER_OF_TRANSACTIONS_TOOLTIP;
    @ViewChild('fileInput') fileInput: ElementRef;
    private readonly LANGUAGES_KEYS: string[] = this.LANGUAGES.map((lang) => lang.key);
    private readonly destroyed$: Subject<boolean> = new Subject();
    private readonly fileUtil: FileUploadUtil;

    constructor(
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly formBuilder: FormBuilder,
        private readonly dialog: MatDialog,
        private readonly uploadService: UploadService,
        private readonly store: Store,
        private readonly cdr: ChangeDetectorRef,
        private readonly localStorageService: LocalStorageService,
    ) {
        this.ruleId = this.route.snapshot.paramMap.get('id');
        this.fileUtil = new FileUploadUtil();
    }

    public ngOnInit(): void {
        this.initForm();
        this.store.dispatch(resetCartStateFlags());
        this.store.dispatch(getAvailableProductLines());
        this.hasAccess = this.checkPermission();
        this.loading$ = this.store.select(selectCartLoadingState);
        this.httpError$ = this.store.select(selectCartError);
        this.availableProducts$ = this.store.select(selectFraudRulesAvailableProductLines);
        this.success$ = this.store.select(selectCartSuccessState);

        if (this.ruleId != null) {
            // Edit mode
            this.isEditMode = true;
            this.store.dispatch(loadFraudRules());

            // Use combineLatest to handle both observables together
            combineLatest([
                this.availableProducts$.pipe(filter((productLines) => productLines != null)),
                this.store.select(selectFraudRuleById(this.ruleId)).pipe(filter((rule) => rule != null)),
            ])
                .pipe(takeUntil(this.destroyed$))
                .subscribe(([productLines, rule]) => {
                    this.availableProductsSingle = new Set([...productLines.singleProductLineNames]);
                    this.availableProductsRepeated = new Set([...productLines.repeatedProductLineNames]);
                    // Add rule's products to the available products sets
                    const setToUpdate =
                        rule.ruleType === FraudRuleTypeEnum.SINGLE ?
                            this.availableProductsSingle
                        :   this.availableProductsRepeated;
                    for (const product of rule.productLineNames) {
                        setToUpdate.add(product);
                    }
                    this.availableProductsSingle = new Set([...this.availableProductsSingle].sort());
                    this.availableProductsRepeated = new Set([...this.availableProductsRepeated].sort());
                    this.checkIfNoAvailableProducts();
                    // Populate form with rule data
                    this.populateFormWithRuleData(rule);
                    this.cdr.markForCheck();
                });
        } else {
            // Create mode - only need available products
            this.availableProducts$
                .pipe(
                    filter((productLines: FraudRulesProductLinesDTO) => productLines != null),
                    takeUntil(this.destroyed$),
                )
                .subscribe(({ singleProductLineNames, repeatedProductLineNames }: FraudRulesProductLinesDTO) => {
                    this.availableProductsSingle = new Set([...singleProductLineNames].sort());
                    this.availableProductsRepeated = new Set([...repeatedProductLineNames].sort());
                    this.checkIfNoAvailableProducts();
                    this.cdr.markForCheck();
                });
        }

        this.httpError$
            .pipe(
                filter((error: HttpErrorResponse) => error != null),
                takeUntil(this.destroyed$),
            )
            .subscribe((error: HttpErrorResponse) => {
                this.handleError(error);
            });
        this.success$
            .pipe(
                filter((success) => success === true),
                takeUntil(this.destroyed$),
            )
            .subscribe(() => {
                this.successMessage = FraudRulesConstants.RULE_CREATION_SUCCESS_MESSAGE;
                this.ruleForm.reset();
                if (this.fileInput?.nativeElement != null) {
                    this.fileInput.nativeElement.value = '';
                }
                this.store.dispatch(getAvailableProductLines());
                window.scrollTo(0, 0);
            });
    }

    public ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    public onRuleTypeChange(): void {
        this.isNoProductsAvailable = false;
        if (this.ruleForm.get('ruleType')?.value === FraudRuleTypeEnum.SINGLE) {
            this.ruleForm.get('numberOfTransactions')?.reset();
            this.ruleForm.get('timeframeInMinutes')?.reset();
        } else {
            this.ruleForm.get('thresholdValue')?.reset();
        }
        this.deselectAllProducts();

        this.checkIfNoAvailableProducts();
    }

    public async onFileSelected(event: Event): Promise<void> {
        this.isImageUploading = true;
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!this.fileUtil.isFileSizeAllowed(file.size) || !(await this.fileUtil.validateFile(file))) {
            this.imageErrorMessage = `Uploaded Image is either unsupported/exceeded maxiumum size`;
            this.isImageUploading = false;
            window.scrollTo(0, 0);
            this.cdr.markForCheck();
            return;
        }

        //generate a filename with the current date and time
        const extension = file?.type.split('/')[1] ?? 'png';
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileName = `fraud-popup-image-${timestamp}.${extension}`;
        if (file != null) {
            // First, get the signed URLs from the backend
            this.uploadService
                .getUploadUrl(fileName, null)
                .pipe(
                    switchMap((response: IUploadURL) =>
                        this.uploadService.uploadFile(response.uploadUrl, file, file.type).pipe(
                            map(() => {
                                const bucketUrl = response.uploadUrl.split(fileName)[0];
                                return bucketUrl + fileName;
                            }),
                        ),
                    ),
                    takeUntil(this.destroyed$),
                )
                .subscribe({
                    next: (fullUrl: string) => {
                        this.imageUrl?.setValue(fullUrl);
                        this.imageErrorMessage = null;
                    },
                    error: () => {
                        this.imageErrorMessage = `Error during file upload`;
                    },
                    complete: () => {
                        this.isImageUploading = false;
                        this.cdr.markForCheck();
                    },
                });
        }
    }

    public removeImage(): void {
        this.imageUrl?.setValue(null);
        if (this.fileInput?.nativeElement != null) {
            this.fileInput.nativeElement.value = '';
        }
    }

    public validateBeforeSaveRule(redirectToManagePage: boolean = true): void {
        if (this.ruleForm.invalid) {
            Object.keys(this.ruleForm.controls).forEach((key) => {
                this.ruleForm.get(key)?.markAsTouched();
            });
            return;
        }

        this.saveRule(redirectToManagePage);
    }

    public cancel(): void {
        this.router.navigate(['/de-ch/fraud-popups']);
    }

    public selectAllProducts(): void {
        if (this.ruleType?.value === FraudRuleTypeEnum.SINGLE) {
            // Set all available products to the form control
            this.productLineNames.setValue([...this.availableProductsSingle]);
        } else if (this.ruleType?.value === FraudRuleTypeEnum.REPEATED) {
            // Set all available products to the form control
            this.productLineNames.setValue([...this.availableProductsRepeated]);
        }
    }

    public deselectAllProducts(): void {
        // Clear the product selection
        this.productLineNames.setValue([]);
    }

    public addText(language: string): void {
        const control = this.ruleForm.get('message').get(language);
        const description = control?.value || '';
        const dialogRef = this.dialog.open(DescriptionEditorDialog, { data: { title: 'Add text', description } });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result != null) {
                    control?.setValue(result);

                    this.LANGUAGES_KEYS.forEach((lang) => {
                        const langControl = this.ruleForm.get('message').get(lang);
                        if (langControl) {
                            langControl.markAsTouched();
                            langControl.markAsDirty();
                        }
                    });

                    this.cdr.markForCheck();
                }
            });
    }

    public hasAnyMessageError(): boolean {
        const messageGroup = this.ruleForm.get('message');
        if (messageGroup == null) return false;

        return this.LANGUAGES_KEYS.some((lang) => {
            const control = messageGroup.get(lang);
            return control != null ? control.invalid && control.touched && control.dirty : false;
        });
    }

    public isMessageInvalid(language: string): boolean {
        const control = this.ruleForm.get('message')?.get(language);
        return control != null ? control.invalid && control.touched && control.dirty : false;
    }

    public resetSuccessMessage(): void {
        this.successMessage = null;
        this.store.dispatch(resetCartStateFlags());
    }

    public resetErrorMessage(): void {
        this.errorMessage = null;
        this.store.dispatch(resetCartStateFlags());
    }

    public get productLineNames(): AbstractControl | null {
        return this.ruleForm?.get('productLineNames');
    }

    public get ruleType(): AbstractControl | null {
        return this.ruleForm?.get('ruleType');
    }

    public get name(): AbstractControl | null {
        return this.ruleForm?.get('name');
    }

    public get thresholdValue(): AbstractControl | null {
        return this.ruleForm?.get('thresholdValue');
    }

    public get numberOfTransactions(): AbstractControl | null {
        return this.ruleForm?.get('numberOfTransactions');
    }

    public get timeframeInMinutes(): AbstractControl | null {
        return this.ruleForm?.get('timeframeInMinutes');
    }

    public get imageUrl(): AbstractControl | null {
        return this.ruleForm?.get('imageUrl');
    }

    private saveRule(redirectToManagePage: boolean): void {
        if (this.thresholdValue?.value === 0) {
            // Show a confirmation dialog if the threshold value is 0
            this.openConfirmationDialogBeforeSaveRule(
                FraudRulesConstants.THRESHOLD_VALUE_ZERO_CONFIRMATION,
                redirectToManagePage,
            );
        }
        // Show a confirmation dialog if the number of transactions is 1
        else if (this.ruleType?.value === FraudRuleTypeEnum.REPEATED && this.numberOfTransactions?.value === 1) {
            this.openConfirmationDialogBeforeSaveRule(
                FraudRulesConstants.NUMBER_OF_TRANSACTIONS_CONFIRMATION,
                redirectToManagePage,
            );
        } else {
            this.dispatchSaveRule(redirectToManagePage);
        }
    }
    private openConfirmationDialogBeforeSaveRule(message: string, redirectToManagePage: boolean): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { title: 'Confirmation', message } });
        dialogRef
            .afterClosed()
            .pipe(
                take(1),
                filter((result: boolean) => result),
            )
            .subscribe(() => {
                this.dispatchSaveRule(redirectToManagePage);
            });
    }

    private checkPermission(): boolean {
        const role = this.localStorageService.getUserRole();
        const priviledgedRoles = [
            UserRoles.DIGITAL_VAUCHERS_ADMIN,
            UserRoles.DIGITAL_VAUCHERS_FRAUD_MANAGER,
            UserRoles.DIGITAL_VAUCHERS_PRODUCT_MANAGER,
        ];
        return PermissionHelper.hasPermission(role, priviledgedRoles);
    }

    private dispatchSaveRule(redirectToManagePage: boolean = true): void {
        const formValue: FraudPopupsFormValue = this.ruleForm.value;
        const ruleData: CreateFraudRuleBaseRequestDTO = {
            name: formValue.name,
            ruleType: formValue.ruleType,
            productLineNames: formValue.productLineNames,
            message: formValue.message,
            imageUrl: formValue.imageUrl,
            ruleConfiguration: this.buildRuleConfiguration(formValue),
        };

        if (this.isEditMode) {
            this.store.dispatch(updateFraudRule({ id: this.ruleId, ruleData }));
        } else {
            this.store.dispatch(createFraudRule({ ruleData, redirectToManagePage }));
        }
    }

    private initForm(): void {
        // Create a validator function to check for whitespace-only values
        const notOnlyWhitespaceValidator = (control: AbstractControl): { [key: string]: any } | null => {
            const isWhitespace = (control.value || '').trim().length === 0;
            return isWhitespace ? { whitespace: true } : null;
        };

        this.ruleForm = this.fb.group({
            ruleType: [null, Validators.required],
            name: ['', [Validators.required, notOnlyWhitespaceValidator]],
            productLineNames: [[], Validators.required],
            thresholdValue: [null],
            numberOfTransactions: [null],
            timeframeInMinutes: [null],
            message: this.formBuilder.group({
                'de-ch': ['', [Validators.required, notOnlyWhitespaceValidator]],
                'fr-ch': ['', [Validators.required, notOnlyWhitespaceValidator]],
                'en-us': ['', [Validators.required, notOnlyWhitespaceValidator]],
                'it-ch': ['', [Validators.required, notOnlyWhitespaceValidator]],
            }),
            imageUrl: [null],
        });

        // Add conditional validation based on rule type
        this.ruleForm
            .get('ruleType')
            ?.valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe((type) => {
                if (type === FraudRuleTypeEnum.SINGLE) {
                    this.ruleForm
                        .get('thresholdValue')
                        ?.setValidators([Validators.required, Validators.min(0), Validators.pattern('^[0-9]*$')]);
                    this.ruleForm.get('numberOfTransactions')?.clearValidators();
                    this.ruleForm.get('timeframeInMinutes')?.clearValidators();
                } else if (type === FraudRuleTypeEnum.REPEATED) {
                    this.ruleForm.get('thresholdValue')?.clearValidators();
                    this.ruleForm
                        .get('numberOfTransactions')
                        ?.setValidators([Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')]);
                    this.ruleForm
                        .get('timeframeInMinutes')
                        ?.setValidators([Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')]);
                }

                this.ruleForm.get('thresholdValue')?.updateValueAndValidity();
                this.ruleForm.get('numberOfTransactions')?.updateValueAndValidity();
                this.ruleForm.get('timeframeInMinutes')?.updateValueAndValidity();
            });
    }

    private checkIfNoAvailableProducts(): void {
        this.productLineNames?.enable();
        this.thresholdValue?.enable();
        this.numberOfTransactions?.enable();
        this.timeframeInMinutes?.enable();
        if (this.availableProductsSingle.size === 0 && this.ruleType?.value === FraudRuleTypeEnum.SINGLE) {
            this.isNoProductsAvailable = true;
            this.thresholdValue?.disable();
            this.productLineNames?.disable();
        } else if (this.availableProductsRepeated.size === 0 && this.ruleType?.value === FraudRuleTypeEnum.REPEATED) {
            this.isNoProductsAvailable = true;
            this.numberOfTransactions?.disable();
            this.timeframeInMinutes?.disable();
            this.productLineNames?.disable();
        }
    }

    private buildRuleConfiguration(
        formValue: FraudPopupsFormValue,
    ): SingleTransactionConfig | RepeatedTransactionsConfig {
        if (formValue.ruleType === FraudRuleTypeEnum.SINGLE) {
            return { thresholdValue: formValue.thresholdValue };
        } else {
            return {
                numberOfTransactions: formValue.numberOfTransactions,
                timeframeInMinutes: formValue.timeframeInMinutes,
            };
        }
    }

    private populateFormWithRuleData(rule: FraudRuleBaseDTO): void {
        let thresholdValue: number | null = null;
        let numberOfTransactions: number | null = null;
        let timeframeInMinutes: number | null = null;

        const messageData: IMultiLanguageDTO = { 'de-ch': '', 'fr-ch': '', 'en-us': '', 'it-ch': '' };

        if (rule.message != null) {
            this.LANGUAGES_KEYS.forEach((lang: string) => {
                messageData[lang] = rule.message[lang];
            });
        }

        if (rule.ruleType === FraudRuleTypeEnum.SINGLE) {
            thresholdValue = (rule.ruleConfiguration as SingleTransactionConfig).thresholdValue;
        } else {
            numberOfTransactions = (rule.ruleConfiguration as RepeatedTransactionsConfig).numberOfTransactions;
            timeframeInMinutes = (rule.ruleConfiguration as RepeatedTransactionsConfig).timeframeInMinutes;
        }

        this.ruleForm.patchValue({
            ruleType: rule.ruleType,
            name: rule.name,
            productLineNames: rule.productLineNames || [],
            thresholdValue: thresholdValue,
            numberOfTransactions: numberOfTransactions,
            timeframeInMinutes: timeframeInMinutes,
            message: messageData,
            imageUrl: rule.imageUrl || null,
        });
        // Note: The product line addition is now handled in the combineLatest subscription
    }
    private handleError(error: HttpErrorResponse): void {
        this.successMessage = null;

        if (error.status === 0) {
            this.errorMessage = FraudRulesConstants.NETWORK_ERROR;
        } else if (error.status === 400) {
            this.errorMessage = error.error?.message || FraudRulesConstants.DEFAULT_ERROR;
        } else {
            this.errorMessage = FraudRulesConstants.DEFAULT_ERROR;
        }
        this.cdr.markForCheck();
    }
}
